import clsx from "clsx";
import { type ReactNode, useLayoutEffect, useMemo, useRef, useState } from "react";

import type { LayoutItem, OrderLayout } from "../../api/types";
import { t } from "../../i18n/i18n";
import { fieldRuns } from "./changes";
import { editorKey } from "./OrderEditor";
import type { UnitMention } from "./UnitPeek";

type TextItem = Extract<LayoutItem, { t: "text" }>;

/** Repère d'une zone de la page (paragraphe mis à jour d'après le dossier) : `fresh`, nouveau pour ce poste (il
 *  pulse deux fois). */
export interface ZoneChange {
  fresh: boolean;
}

/** Abscisse du trait de révision, dans la marge gauche (points). */
const REVISION_X = 19.5;

function weightOf(weight: TextItem["weight"]): number {
  return weight === "bold" || weight === "semibold" ? 600 : 400;
}

/** Une page A4 de l'ordre, dessinée depuis la liste d'affichage du serveur : identique au PDF. Les paragraphes mis
 *  à jour d'après le dossier (`changed`, par étiquette de la mise en page) sont surlignés sous le texte, avec un trait
 *  de révision dans la marge : à l'écran seulement, jamais dans le PDF. */
export function OrderPage({
  page,
  layout,
  imageUrl,
  activeField,
  onPick,
  editable,
  editableFields,
  className,
  units,
  onUnitHover,
  changed,
  hidingChanges = false,
}: {
  page: OrderLayout["pages"][number];
  layout: OrderLayout;
  imageUrl: (name: string) => string | null;
  activeField: string | null;
  onPick: (field: string) => void;
  editable: boolean;
  /** Champs que l'éditeur sait modifier : les autres (matrice de la rotation CENTAC…) ne sont pas cliquables. */
  editableFields?: ReadonlySet<string>;
  /** Classe ajoutée à la page (apparition de la première page). */
  className?: string;
  /** Unités que l'ordre cite : leur nom survolé montre une mini-carte (09/10/2026). */
  units?: readonly UnitMention[];
  onUnitHover?: (mention: UnitMention | null, anchor: DOMRect | null) => void;
  /** Paragraphes mis à jour d'après le dossier : étiquette de la mise en page → repère (tous les blocs du champ). */
  changed?: ReadonlyMap<string, ZoneChange>;
  /** « Masquer les repères » : les surlignages s'effacent en fondu. */
  hidingChanges?: boolean;
}) {
  const { width, height } = layout;
  const [hover, setHover] = useState<string | null>(null);
  const runs = useMemo(() => fieldRuns(page.items, height), [page.items, height]);
  const clickable = useMemo(
    () => runs.filter((z) => !editableFields || editableFields.has(editorKey(z.field))),
    [runs, editableFields],
  );
  // Blocs mis à jour, jamais filtrés par les champs de l'éditeur (matrice CENTAC, titres des temps…).
  const marked = useMemo(() => (changed?.size ? runs.filter((z) => changed.has(z.field)) : []), [runs, changed]);

  // Noms d'unités dans le texte : découpés en <tspan data-unit>, puis couverts (au-dessus des zones d'édition)
  // d'un rectangle de survol à leur emprise mesurée.
  const svgRef = useRef<SVGSVGElement>(null);
  const matcher = useMemo(() => {
    const labels = (units ?? []).flatMap((u, i) => u.labels.map((label) => [label, i] as const));
    if (!labels.length) return null;
    labels.sort((a, b) => b[0].length - a[0].length);
    const index = new Map(labels.map(([label, i]) => [label, i]));
    const alternatives = labels.map(([label]) => label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|");
    return { re: new RegExp(`(?<![\\p{L}\\p{N}(])(${alternatives})(?![\\p{L}\\p{N}])`, "gu"), index };
  }, [units]);
  const segments = (text: string): ReactNode => {
    if (!matcher) return text;
    const parts: ReactNode[] = [];
    let last = 0;
    for (const m of text.matchAll(matcher.re)) {
      const start = m.index ?? 0;
      if (start > last) parts.push(text.slice(last, start));
      parts.push(
        <tspan key={start} data-unit={matcher.index.get(m[1]!)}>
          {m[1]}
        </tspan>,
      );
      last = start + m[1]!.length;
    }
    if (!parts.length) return text;
    if (last < text.length) parts.push(text.slice(last));
    return parts;
  };
  const [unitBoxes, setUnitBoxes] = useState<{ x: number; y: number; w: number; h: number; unit: number }[]>([]);
  useLayoutEffect(() => {
    const svg = svgRef.current;
    if (!svg || !matcher) {
      setUnitBoxes([]);
      return;
    }
    const boxes: { x: number; y: number; w: number; h: number; unit: number }[] = [];
    svg.querySelectorAll<SVGTSpanElement>("tspan[data-unit]").forEach((span) => {
      if (typeof span.getBBox !== "function") return;
      const b = span.getBBox();
      if (b.width > 0) boxes.push({ x: b.x, y: b.y, w: b.width, h: b.height, unit: Number(span.dataset.unit) });
    });
    setUnitBoxes(boxes);
  }, [page, matcher]);

  return (
    <svg
      ref={svgRef}
      viewBox={`0 0 ${width} ${height}`}
      className={clsx("block h-auto w-full bg-white shadow-paper select-text", className)}
      style={{ letterSpacing: "normal", fontStretch: "normal" }}
      role="img"
      aria-label={t("Page {n}", { n: page.number })}
    >
      {marked.map((z, i) => (
        <g
          key={`c-${z.field}-${i}`}
          data-change-zone={z.field}
          className={clsx("opo-change", hidingChanges && "opo-change-out")}
        >
          {/* Teinte à part : la pulsation « nouveau » s'y pose et s'en retire sans rejouer l'apparition du groupe. */}
          <g className={clsx("opo-change-tint", changed?.get(z.field)?.fresh && "opo-change-new")}>
            <rect x={z.x} y={z.y} width={z.w} height={z.h} rx={2} fill="var(--color-mark)" fillOpacity={0.45} />
            <rect x={REVISION_X} y={z.y} width={2.5} height={z.h} rx={1.25} fill="var(--color-mark)" />
          </g>
        </g>
      ))}
      {clickable.map((z, i) =>
        z.field === activeField || z.field === hover ? (
          <rect
            key={`h-${z.field}-${i}`}
            x={z.x}
            y={z.y}
            width={z.w}
            height={z.h}
            rx={2}
            fill="var(--color-accent)"
            fillOpacity={z.field === activeField ? 0.12 : 0.06}
          />
        ) : null,
      )}
      {page.items.map((it, i) => {
        if (it.t === "rect") {
          return (
            <rect
              key={i}
              x={it.x}
              y={height - it.y - it.h}
              width={it.w}
              height={it.h}
              fill={it.fill ?? "none"}
              fillOpacity={it.fillAlpha ?? undefined}
              stroke={it.stroke ?? "none"}
              strokeOpacity={it.strokeAlpha ?? undefined}
              strokeWidth={it.lw}
            />
          );
        }
        if (it.t === "image") {
          const href = imageUrl(it.name);
          if (!href) return null;
          return (
            <image
              key={i}
              href={href}
              x={it.x}
              y={height - it.y - it.h}
              width={it.w}
              height={it.h}
              opacity={it.opacity}
              preserveAspectRatio="xMidYMid meet"
            />
          );
        }
        return (
          <g key={i}>
            <text
              x={it.x}
              y={height - it.y}
              fontFamily='"Inter OPO", "Inter", system-ui, sans-serif'
              fontSize={it.size}
              fontWeight={weightOf(it.weight)}
              fontStyle={it.weight === "italic" ? "italic" : "normal"}
              fill={it.color}
              fillOpacity={it.alpha}
              textLength={it.w > 0 ? it.w : undefined}
              lengthAdjust="spacingAndGlyphs"
              // Ligne justifiée : l'espace ajoutée entre les mots (points) mène la ligne à la largeur de la colonne.
              style={{ whiteSpace: "pre", wordSpacing: it.ws ? `${it.ws}px` : undefined }}
            >
              {segments(it.text)}
            </text>
            {it.u && (
              <line
                x1={it.x}
                x2={it.x + it.w}
                y1={height - it.y + it.size / 12}
                y2={height - it.y + it.size / 12}
                stroke={it.color}
                strokeWidth={it.size / 15}
              />
            )}
          </g>
        );
      })}
      {editable &&
        clickable.map((z, i) => (
          <rect
            key={`z-${z.field}-${i}`}
            x={z.x}
            y={z.y}
            width={z.w}
            height={z.h}
            fill="transparent"
            className="cursor-text"
            onMouseEnter={() => setHover(z.field)}
            onMouseLeave={() => setHover((h) => (h === z.field ? null : h))}
            onClick={() => onPick(z.field)}
          >
            <title>{t("Modifier ce paragraphe")}</title>
          </rect>
        ))}
      {units &&
        unitBoxes.map((b, i) => (
          <rect
            key={`u-${i}`}
            x={b.x}
            y={b.y}
            width={b.w}
            height={b.h}
            fill="var(--color-accent)"
            fillOpacity={0}
            className="cursor-pointer transition-[fill-opacity] duration-150 hover:[fill-opacity:0.14]"
            onMouseEnter={(e) => onUnitHover?.(units[b.unit] ?? null, e.currentTarget.getBoundingClientRect())}
            onMouseLeave={() => onUnitHover?.(null, null)}
            onClick={() => {
              // Clic sur le nom : le paragraphe qui le contient s'ouvre à l'édition, comme ailleurs dans la page.
              const zone = clickable.find((z) => b.x >= z.x && b.x <= z.x + z.w && b.y >= z.y - 2 && b.y <= z.y + z.h);
              if (editable && zone) onPick(zone.field);
            }}
          />
        ))}
    </svg>
  );
}
